// components/FloatingCartButton.jsx
import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { useTheme } from "../hooks/useTheme";
import { useCart } from "../contexts/CartContext";

/**
 * Floating cart button — bottom right.
 *
 * Badge shows item count from CartContext. Animates (scale pop) whenever
 * the count changes so the user notices items landing in the cart.
 *
 * Hides itself on the cart page and on login/register.
 */
export default function FloatingCartButton() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useCart();
  const btnRef = useRef(null);
  const prevCount = useRef(count);

  // Pop animation on count change
  useEffect(() => {
    if (count === prevCount.current) return;
    const el = btnRef.current;
    if (!el) return;
    el.style.transform = "scale(1.15)";
    const t = setTimeout(() => {
      if (el) el.style.transform = "scale(1)";
    }, 140);
    prevCount.current = count;
    return () => clearTimeout(t);
  }, [count]);

  // Hide on cart, login, register
  const hiddenPaths = ["/cart", "/login", "/register"];
  if (hiddenPaths.some((p) => location.pathname === p)) return null;

  const label =
    count > 99 ? "99+" : String(count);

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={() => navigate("/cart")}
      aria-label={count > 0 ? `Cart, ${count} items` : "Cart"}
      className="sf-floating-cart"
      style={styles.btn(colors)}
    >
      <ShoppingCart size={20} />
      {count > 0 && <span style={styles.badge(colors)}>{label}</span>}
    </button>
  );
}

const styles = {
  btn: (c) => ({
    position: "fixed",
    right: 16,
    /* Sits above the mobile bottom nav (60px tall) plus its safe area */
    bottom: "calc(76px + env(safe-area-inset-bottom, 0px))",
    zIndex: 90,
    width: 54,
    height: 54,
    borderRadius: "50%",
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 8px 24px ${c.btnBg}55, 0 2px 6px rgba(0,0,0,0.12)`,
    transition: "transform 0.14s ease, box-shadow 0.15s ease",
    fontFamily: "inherit",
  }),
  badge: (c) => ({
    position: "absolute",
    top: -2,
    right: -2,
    minWidth: 22,
    height: 22,
    padding: "0 6px",
    borderRadius: 11,
    background: c.error,
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `2px solid ${c.raisedBg}`,
    boxSizing: "border-box",
  }),
};

// Desktop: sits lower since there's no bottom nav to avoid
if (typeof window !== "undefined" && !document.getElementById("sf-floating-cart-style")) {
  const el = document.createElement("style");
  el.id = "sf-floating-cart-style";
  el.textContent = `
    @media (min-width: 1024px) {
      .sf-floating-cart {
        bottom: 24px !important;
      }
    }
  `;
  document.head.appendChild(el);
}